import React from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import type { ThemeAIStandardCode } from '../../types/gotypes'
import { emotionColour, sentimentColour } from '../../utils/themeAISentimentColours'

export type StandardFrameKind = 'sentiment' | 'emotion'

interface StandardCodeFrameDialogProps {
  kind?: StandardFrameKind
  codes?: ThemeAIStandardCode[]
  /** The framework description the model is given with the codes. */
  description?: string
  onClose: () => void
}

const TITLES: Record<StandardFrameKind, string> = { sentiment: 'Sentiment', emotion: 'Emotion' }

const label = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

// The whole standard sentiment or emotion code frame a switched-on run option
// codes with, opened from the Run step (Matt, 24 Sep: "show them the code frame
// in a modal so they can see the detail of the whole thing"). Read-only: the
// standard frames are the same for every client. Each code is drawn in the
// colour the results use for it.
const StandardCodeFrameDialog: React.FC<StandardCodeFrameDialogProps> = ({ kind, codes, description, onClose }) => {
  const colourFor = kind === 'emotion' ? emotionColour : sentimentColour
  // Sentiment stores a number ("100") under a name ("Positive"); an emotion's
  // code is its name, so its code column would repeat the name.
  const showCode = (codes || []).some((code) => code.code.toLowerCase() !== code.text.toLowerCase())

  return (
    <Dialog open={Boolean(kind)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{kind ? `${TITLES[kind]} code frame` : ''}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '65ch' }}>
          The standard {kind} code frame, the same for every client. Where a response matches a theme, the words
          that matched get one of these codes, so a response can be coded differently for each theme it matches.
          The model is given each code's description and the framework description below.
        </Typography>
        <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {(codes || []).map((code) => (
            <Box
              component="li"
              key={code.code}
              sx={{
                display: 'grid',
                gridTemplateColumns: showCode ? '12px 1fr auto' : '12px 1fr',
                columnGap: 1.5,
                py: 1.25,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box
                aria-hidden
                sx={{ width: 12, height: 12, mt: '4px', borderRadius: '50%', bgcolor: colourFor(code.text) || 'action.disabled' }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{label(code.text)}</Typography>
                {code.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{code.description}</Typography>
                )}
              </Box>
              {showCode && (
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }} title="The code stored in the data">
                  {code.code}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
        {description && (
          // Matt, 24 Sep (#1523 item 10): "this is the framework description,
          // and we stick that in because it's brilliant". It is what the model
          // reads with the codes, so it is shown as written.
          <Box component="section" aria-label="Framework description">
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Framework description</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'pre-wrap', maxWidth: '75ch', p: 1.5, borderRadius: 1, backgroundColor: 'action.hover' }}
            >
              {description}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="info" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default StandardCodeFrameDialog
